import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { UserActivityLog, User, COLLECTION_NAMES } from '../types';

export type TimelineFilter = '24h' | '7d' | '30d' | '90d' | 'all';
export type ActivityTypeFilter = 'all' | 'rock' | 'issue' | 'action_item' | 'metric' | 'wins_excited';

/**
 * Hook for managing Activity Logs data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useActivityLogs(
  selectedUserId?: string | null,
  timelineFilter: TimelineFilter = '7d',
  activityTypeFilter: ActivityTypeFilter = 'all'
) {
  const [activityLogs, setActivityLogs] = useState<UserActivityLog[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load activity logs with real-time listener and filtering
  useEffect(() => {
    setLoading(true);

    const constraints: QueryConstraint[] = [];

    // Filter by user if selected
    if (selectedUserId) {
      constraints.push(where('userId', '==', selectedUserId));
    }

    // Filter by timeline
    if (timelineFilter !== 'all') {
      const now = new Date();
      let startDate = new Date();

      switch (timelineFilter) {
        case '24h':
          startDate.setHours(now.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
      }

      constraints.push(where('createdAt', '>=', Timestamp.fromDate(startDate)));
    }

    // Filter by activity type
    // Note: Firestore 'in' queries are limited to 10 items, but our activity types are well under that
    if (activityTypeFilter !== 'all') {
      const activityTypes: string[] = [];
      switch (activityTypeFilter) {
        case 'rock':
          activityTypes.push('rock_created', 'rock_completed', 'rock_dropped');
          break;
        case 'issue':
          activityTypes.push('issue_created', 'issue_resolved');
          break;
        case 'action_item':
          activityTypes.push('action_item_created', 'action_item_completed');
          break;
        case 'metric':
          activityTypes.push('metric_created');
          break;
        case 'wins_excited':
          activityTypes.push('wins_excited_created');
          break;
      }

      if (activityTypes.length > 0 && activityTypes.length <= 10) {
        constraints.push(where('activityType', 'in', activityTypes));
      }
    }

    // Order by createdAt descending and limit results
    constraints.push(orderBy('createdAt', 'desc'));
    constraints.push(limit(500));

    const activityLogsQuery = query(
      collection(db, COLLECTION_NAMES.USER_ACTIVITY_LOGS),
      ...constraints
    );

    const unsubscribe = onSnapshot(
      activityLogsQuery,
      (snapshot) => {
        const logsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
          } as UserActivityLog;
        });

        setActivityLogs(logsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading activity logs:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedUserId, timelineFilter, activityTypeFilter, eosUsers]);

  return {
    activityLogs,
    users: eosUsers,
    loading,
  };
}

